import ActionDialog from "@dashboard/components/ActionDialog";
import {
  ProductTypeKindEnum,
  useProductTypeCreateMutation,
  useProductTypeDeleteMutation,
  useProductTypeListQuery,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Button, Checkbox, Input, Skeleton, Text } from "@saleor/macaw-ui-next";
import { Package, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { EmptyState } from "../../components/EmptyState";
import { Pagination } from "../../components/Pagination";
import { useWizardContext } from "../../context/WizardContext";
import { messages } from "./messages";

const PAGE_SIZE = 10;

interface ProductTypesTabProps {
  formId: string;
}

export const ProductTypesTab: React.FC<ProductTypesTabProps> = ({ formId }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { completeCurrentStep, goToNextStep } = useWizardContext();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [isShippingRequired, setIsShippingRequired] = useState(true);
  const [nameError, setNameError] = useState("");
  const [paginationVars, setPaginationVars] = useState<{
    first?: number;
    after?: string;
    last?: number;
    before?: string;
  }>({ first: PAGE_SIZE });

  const { data, loading, refetch } = useProductTypeListQuery({
    variables: paginationVars,
  });

  const [createProductType, { loading: creating }] = useProductTypeCreateMutation();
  const [deleteProductType, { loading: deleting }] = useProductTypeDeleteMutation();
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const productTypes = mapEdgesToItems(data?.productTypes) ?? [];
  const pageInfo = data?.productTypes?.pageInfo;

  const handleStepSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    completeCurrentStep();
    goToNextStep();
  };

  const resetForm = () => {
    setName("");
    setIsShippingRequired(true);
    setNameError("");
    setShowForm(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setNameError(intl.formatMessage(messages.nameRequired));

      return;
    }

    const result = await createProductType({
      variables: {
        input: {
          name: name.trim(),
          hasVariants: false,
          isShippingRequired,
          kind: ProductTypeKindEnum.NORMAL,
        },
      },
    });

    const errors = result.data?.productTypeCreate?.errors ?? [];

    if (errors.length > 0) {
      notify({
        status: "error",
        text: errors[0]?.message ?? intl.formatMessage(messages.createError),
      });

      return;
    }

    notify({
      status: "success",
      text: intl.formatMessage(messages.productTypeCreated),
    });
    resetForm();
    refetch();
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;

    const result = await deleteProductType({ variables: { id: deleteTargetId } });
    const errors = result.data?.productTypeDelete?.errors ?? [];

    if (errors.length > 0) {
      notify({
        status: "error",
        text: errors[0]?.message ?? intl.formatMessage(messages.deleteError),
      });
    } else {
      notify({
        status: "success",
        text: intl.formatMessage(messages.productTypeDeleted),
      });
      refetch();
    }

    setDeleteTargetId(null);
  };

  if (showForm) {
    return (
      <>
        <form id={formId} onSubmit={handleStepSubmit} />
        <Box
          display="flex"
          flexDirection="column"
          gap={4}
          padding={5}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={4}
        >
          <Input
            label={intl.formatMessage(messages.productTypeName)}
            value={name}
            onChange={e => {
              setName(e.target.value);

              if (nameError) setNameError("");
            }}
            error={!!nameError}
            helperText={nameError}
            disabled={creating}
            size="large"
          />

          <Checkbox
            checked={isShippingRequired}
            onCheckedChange={checked => setIsShippingRequired(!!checked)}
            disabled={creating}
          >
            <Text size={2}>{intl.formatMessage(messages.isShippingRequired)}</Text>
          </Checkbox>

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button variant="secondary" size="small" onClick={resetForm} disabled={creating}>
              {intl.formatMessage(messages.cancel)}
            </Button>
            <Button variant="primary" size="small" onClick={handleCreate} disabled={creating}>
              {intl.formatMessage(messages.save)}
            </Button>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <form id={formId} onSubmit={handleStepSubmit} />

      <Box
        display="flex"
        alignItems={{ mobile: "flex-start", tablet: "center" }}
        justifyContent="space-between"
        flexDirection={{ mobile: "column", tablet: "row" }}
        gap={3}
        marginBottom={4}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Text size={2} fontWeight="bold" color="default1" __textTransform="uppercase">
            Brands
          </Text>
          <Text size={2} color="default2">
            Review and manage the Brands used in your Store.
          </Text>
        </Box>
        <Button variant="primary" size="large" onClick={() => setShowForm(true)}>
          {intl.formatMessage(messages.addProductType)}
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" gap={2} padding={4}>
          <Skeleton __height="40px" />
          <Skeleton __height="40px" />
          <Skeleton __height="40px" />
        </Box>
      ) : productTypes.length === 0 ? (
        <EmptyState icon={Package} message={intl.formatMessage(messages.emptyProductTypes)} />
      ) : (
        <Box
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={4}
          __overflow="hidden"
        >
          <Box
            display="grid"
            __gridTemplateColumns="2fr 1fr auto"
            gap={2}
            paddingX={4}
            paddingY={3}
            __backgroundColor="rgba(148, 163, 184, 0.06)"
            borderBottomWidth={1}
            borderBottomStyle="solid"
            borderColor="default1"
          >
            <Text size={2} fontWeight="bold" color="default2">
              {intl.formatMessage(messages.nameColumn)}
            </Text>
            <Text size={2} fontWeight="bold" color="default2">
              {intl.formatMessage(messages.shippableColumn)}
            </Text>
            <Box __width="32px" />
          </Box>
          {productTypes.map((pt, index) => (
            <Box
              key={pt.id}
              display="grid"
              __gridTemplateColumns="2fr 1fr auto"
              gap={2}
              paddingX={4}
              paddingY={3}
              borderBottomWidth={index < productTypes.length - 1 ? 1 : 0}
              borderBottomStyle="solid"
              borderColor="default1"
              alignItems="center"
            >
              <Text size={2}>{pt.name}</Text>
              <Text size={2}>
                {pt.isShippingRequired
                  ? intl.formatMessage(messages.yes)
                  : intl.formatMessage(messages.no)}
              </Text>
              <Box
                as="button"
                cursor="pointer"
                onClick={() => setDeleteTargetId(pt.id)}
                __backgroundColor="transparent"
                __padding="4px"
                style={{ border: "none" }}
                borderRadius={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Trash2 size={16} color="#e74c3c" />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {pageInfo && (
        <Pagination
          hasNextPage={pageInfo.hasNextPage}
          hasPreviousPage={pageInfo.hasPreviousPage}
          onNext={() =>
            setPaginationVars({
              first: PAGE_SIZE,
              after: pageInfo.endCursor ?? undefined,
            })
          }
          onPrevious={() =>
            setPaginationVars({
              last: PAGE_SIZE,
              before: pageInfo.startCursor ?? undefined,
            })
          }
          disabled={loading}
        />
      )}

      <ActionDialog
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDelete}
        confirmButtonState={deleting ? "loading" : "default"}
        variant="delete"
        title={intl.formatMessage(messages.deleteConfirmTitle)}
      >
        <Text>{intl.formatMessage(messages.deleteConfirmProductType)}</Text>
      </ActionDialog>
    </>
  );
};
